import React from "react";

import "dhx-suite/codebase/suite.min.css";

import { AppModel, EmbeddedMode, JsSettings, Snippet } from "../models/index";
import { DispatchContext } from "../store";
import { SiteView } from "./SiteView";
import { SiteWideView } from "./SiteWideView";
import { SearchView } from "./search-page/SearchView";

import config from "../config";
import { getPreviewContent } from "../services/previewService";
import { CodeEditor } from "./CodeEditor";
import { PreviewFrame } from "./PreviewFrame";
import { Action } from "../store/actions";
import { EmbeddedContainer } from "./EmbeddedContainer";
import { capitalize } from "../utils/stringHelper";
import { isIE } from "../utils/isIE";
import { PreviewPanel } from "./PreviewPanel";

export interface MainViewProps {
  state: AppModel;
}

function getVersionName(settings: JsSettings) {
  const widgets = settings.widgets
    .map((w) => {
      const widget = remote.data.versions.find(
        (v) => v.widget === w.name && v.id === w.version
      );

      return widget && `${capitalize(widget.widget)} ${widget.value}`;
    })
    .filter((x) => x);

  return widgets.join(", ");
}

export function getEmbeddedContent(
  mode: EmbeddedMode,
  snippet: Snippet,
  previewInitialized: boolean,
  dispatch: (action: Action) => void,
  darkTheme: boolean,
  scriptErrorLine?: number
) {
  switch (mode) {
    case "result":
      return (
        <PreviewFrame
          iframeSrc={`${config.secureDomain}/snippet.html?${config.version}`}
          content={getPreviewContent(snippet)}
          previewInitialized={previewInitialized}
        />
      );
    case "htmlCode":
      return (
        <CodeEditor
          mode="html"
          onChange={(value: string) =>
            dispatch({
              type: "HtmlCodeChanged",
              code: value,
            })
          }
          value={snippet.htmlCode}
          isDarkMode={darkTheme}
          className="st-code-editor__embedded"
          type={isIE ? "codeMirror" : "monaco"}
        />
      );
    case "jsCode":
      return (
        <CodeEditor
          mode="js"
          value={snippet.jsCode}
          onChange={(value: string) =>
            dispatch({
              type: "JsCodeChanged",
              code: value,
            })
          }
          errorLine={scriptErrorLine}
          isDarkMode={darkTheme}
          className="st-code-editor__embedded"
          type={isIE ? "codeMirror" : "monaco"}
          jsSettings={snippet.jsSettings}
        />
      );
  }
}

function getEmbeddedView(
  state: AppModel,
  mode: EmbeddedMode,
  dispatch: (action: Action) => void
) {
  return state.snippet && state.snippet.id ? (
    <EmbeddedContainer state={state} mode={mode}>
      {getEmbeddedContent(
        mode,
        state.snippet,
        state.previewInitialized,
        dispatch,
        state.darkTheme,
        state.scriptErrorLine
      )}
    </EmbeddedContainer>
  ) : (
    <div>Loading...</div>
  );
}

export const MainView = ({ state }: MainViewProps) => {
  const { dispatch } = React.useContext(DispatchContext);

  switch (state.mode) {
    case "search":
      return <SearchView state={state} />;
    case "htmlCode":
    case "jsCode":
    case "result":
      return getEmbeddedView(state, state.mode, dispatch);
    case "mobile":
      return (
        <PreviewPanel
          iframeSrc={`${config.secureDomain}/snippet.html?${config.version}`}
          content={getPreviewContent(state.snippet)}
          cooling={state.previewCooling}
          previewInitialized={state.previewInitialized}
        />
      );
    default: {
      return (
        <SiteView
          state={state}
          versions={getVersionName(state.snippet.jsSettings)}
        />
      );
    }
  }
};
